import React, { useState, useRef, useEffect, useCallback, useMemo, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, Clock, FileText, MagnifyingGlass,
  NotePencil, Palette, Presentation, Sparkle, Table,
} from '@phosphor-icons/react';
import { AppSquircle } from '../../AppSquircle';
import type { BaseEntityType } from '../../../types/entities';
import { useEntityIcon } from '../../../hooks/useEntityIcon';
import { useSpaceEntities } from '../../../stores/spaceEntityStore';
import useSpaceStore from '../../../stores/spaceStore';
import useDataspaceTreeStore from '../../../stores/dataspaceTreeStore';
import { useAppRegistryStore } from '../../../stores/appRegistryStore';
import { useCommandRegistryStore } from '../../../stores/commandRegistryStore';
import { entityCreationRegistry, type EntityCreationMetadata } from '../../../services/entityCreationRegistry';
import { entityService } from '../../../services/entityService';
import type { EntitySearchResult } from '../../../services/entityService';
import { searchUniversal } from '../../../services/searchService';
import { createFileLogger } from '../../../utils/logger';
import './GlobalEntitySearch.scss';
import { entityActions } from '../../../stores/entityActions';
import { interceptLLMRequest } from '../../../stores/usageLimitsStore';
import { useGlobalEntitySearchStore } from '../../../stores/globalEntitySearchStore';
import { setNextWindowPlacement } from '../../../stores/windowPlacement';
import { useOrgNodes } from '../../../stores/entityRouter';
import { HIDDEN_ENTITY_TYPES } from '../../../utils/entityVisibility';
import { entityRegistry } from '../../../stores/entityRegistry';
import { requireAccount } from '../../../stores/guestGateStore';
import { isGuestSession, getGuestSpaceId } from '../../../hooks/useGuestMode';
import useUserStore from '../../../stores/userStore';

const log = createFileLogger('GLOBAL_ENTITY_SEARCH');

// Entity types belonging to an experimental app (see TranscriptApp/definition.ts) —
// excluded from search results unless the session can use experimental features,
// so existing entities don't leak through here even though the app itself is only
// hidden from creation surfaces (Dock, Create Menu).
const EXPERIMENTAL_ENTITY_TYPES = new Set(['Transcript']);

/** True when an entity type belongs to an experimental app the session can't use yet. */
export function isExperimentallyGatedEntityType(
  entityType: string | undefined,
  canUseExperimental: boolean,
): boolean {
  return !!entityType && EXPERIMENTAL_ENTITY_TYPES.has(entityType) && !canUseExperimental;
}

/**
 * Fuzzy entity name scoring for client-side filtering.
 * Returns 0 (no match) or a positive score. Higher = better match.
 *
 * Tiers (highest to lowest):
 *   100 — exact substring (e.g. "doc" ↔ "My Document")
 *    80 — all query words are substrings (multi-word)
 *    70 — acronym: query matches word initials (e.g. "mr" ↔ "Monthly Report")
 *    50 — character subsequence (e.g. "mthly" ↔ "Monthly")
 */
function fuzzyScore(name: string, query: string): number {
  const n = name.toLowerCase();
  const q = query.toLowerCase();
  if (!q) return 0;

  if (n.includes(q)) return 100;

  const qWords = q.split(/\s+/).filter(Boolean);
  if (qWords.length > 1 && qWords.every(w => n.includes(w))) return 80;

  const initials = n.split(/[\s\-_.]+/).filter(Boolean).map(w => w[0]).join('');
  if (initials.length >= q.length && (initials.startsWith(q) || initials.includes(q))) return 70;

  let qi = 0;
  for (let ni = 0; ni < n.length && qi < q.length; ni++) {
    if (n[ni] === q[qi]) qi++;
  }
  if (qi === q.length) return Math.max(30, 55 - q.length * 2);

  return 0;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  Doc: 'Document', Note: 'Note', Page: 'Page', File: 'File',
  Contact: 'Contact', Task: 'Task', Goal: 'Goal', Project: 'Project',
  Agent: 'Agent', Thread: 'Thread', Space: 'Desktop', Group: 'Folder',
};

function typeLabel(et: string): string {
  if (ENTITY_TYPE_LABELS[et]) return ENTITY_TYPE_LABELS[et];
  if (et.startsWith('File.')) return 'File';
  if (et.startsWith('Integration.')) return et.replace('Integration.', '');
  return et;
}

// ── Quickbar item definitions ─────────────────────────────────────────────────

interface QuickbarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const CREATE_ITEMS: QuickbarItem[] = [
  { id: 'new-doc',    label: 'Doc',    icon: <FileText    size={16} weight="duotone" style={{ color: '#2563eb' }} /> },
  { id: 'new-note',   label: 'Note',   icon: <NotePencil  size={16} weight="duotone" style={{ color: '#f59e0b' }} /> },
  { id: 'new-sheet',  label: 'Sheet',  icon: <Table       size={16} weight="duotone" style={{ color: '#10b981' }} /> },
  { id: 'new-canvas', label: 'Canvas', icon: <Palette     size={16} weight="duotone" style={{ color: '#9333ea' }} /> },
  { id: 'new-slides', label: 'Slides', icon: <Presentation size={16} weight="duotone" style={{ color: '#f97316' }} /> },
];

const QUICKBAR_ITEMS: QuickbarItem[] = CREATE_ITEMS;

// ── Launchable apps (searchable "Apps" results section) ───────────────────────
//
// Apps surfaced in the results list are sourced from the app registry. We hide
// internal shells and per-entity editors: those open empty without an entity,
// so they're reached through the "Actions" (create) section instead.
const LAUNCHABLE_APP_BLOCKLIST = new Set<string>([
  // internal / non-launchable shells
  'entity-view', 'new-window', 'quicklauncher', 'notifications', 'store',
  'ingestWizard', 'app-builder', 'entity-graph', 'terminal',
  // per-entity editors — created via Actions, not opened empty
  'docs', 'notes-v2', 'notes-markdown', 'markdown-v2', 'page-v2',
  'canvas', 'canvas-v2', 'sheets-v2', 'presentations-v2', 'diagram',
  'pdfium', 'webdocument', 'transcript', 'imagestudio', 'media-gen',
  'image-studio', 'file-viewer', 'file-editor', 'legal-code-viewer',
  'google-drive-file', 'local-file',
  // socialstudio opens via dock / chip submit, not empty-window launch
  'socialstudio',
]);

interface LaunchableApp {
  appId: string;
  name: string;
  description: string;
}

// ── Drive view items — searchable navigation targets within RiverDriveApp ─────

interface DriveViewItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  view: { mode: string; [key: string]: any };
}

const DRIVE_SQUIRCLE = <AppSquircle appId="entityBrowser2" size="sm" />;

const DRIVE_VIEW_ITEMS: DriveViewItem[] = [
  { id: 'drive-files',        label: 'My Drive',          icon: DRIVE_SQUIRCLE, view: { mode: 'files' } },
  { id: 'drive-recents',      label: 'Recents',            icon: DRIVE_SQUIRCLE, view: { mode: 'recents' } },
  { id: 'drive-shared',       label: 'Shared with Me',     icon: DRIVE_SQUIRCLE, view: { mode: 'shared' } },
  { id: 'drive-web',          label: 'Browsing History',   icon: DRIVE_SQUIRCLE, view: { mode: 'web' } },
  { id: 'drive-contacts',     label: 'Contacts',           icon: DRIVE_SQUIRCLE, view: { mode: 'people' } },
  { id: 'drive-work',         label: 'Tasks & Goals',      icon: DRIVE_SQUIRCLE, view: { mode: 'work' } },
  { id: 'drive-agents',       label: 'Automations',        icon: DRIVE_SQUIRCLE, view: { mode: 'automations' } },
  { id: 'drive-skills',       label: 'Skills',             icon: DRIVE_SQUIRCLE, view: { mode: 'skills' } },
  { id: 'drive-knowledge',    label: 'Knowledge',          icon: DRIVE_SQUIRCLE, view: { mode: 'knowledge' } },
  { id: 'drive-integrations', label: 'Integrations',       icon: DRIVE_SQUIRCLE, view: { mode: 'integrations' } },
  { id: 'drive-activity',     label: 'Activity',           icon: DRIVE_SQUIRCLE, view: { mode: 'activity' } },
  { id: 'drive-import',       label: 'Import & Sources',   icon: DRIVE_SQUIRCLE, view: { mode: 'import-sources' } },
  { id: 'drive-tools-dev',    label: 'Tool Registry',      icon: DRIVE_SQUIRCLE, view: { mode: 'tools-dev' } },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface GlobalEntitySearchProps {
  onClose: () => void;
}

type ActiveZone = 'quickbar' | 'results' | null;

const GlobalEntitySearch: React.FC<GlobalEntitySearchProps> = ({ onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [activeZone, setActiveZone] = useState<ActiveZone>(null);
  const [quickbarIndex, setQuickbarIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [backendResults, setBackendResults] = useState<EntitySearchResult[]>([]);
  const [backendLoading, setBackendLoading] = useState(false);
  const [isShiftHeld, setIsShiftHeld] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const quickbarRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Deferred query drives every heavy client-side filter so the input box and
  // overlay paint on the first frame even in spaces with thousands of entities;
  // React fills the results in on a later, lower-priority render.
  const deferredInput = useDeferredValue(inputValue);
  const query = deferredInput.trim();

  const navigate = useNavigate();
  const { executeCommand } = useCommandRegistryStore();
  const activeSpace = useSpaceStore(s => s.activeSpace);
  const currentSpaceId = activeSpace?.id;
  const hasActiveSpace = !!activeSpace;

  // App catalog (loaded at boot via appRegistryStore.initializeApps). Used to
  // surface launchable apps in the results list.
  const appsCatalog = useAppRegistryStore(s => s.apps);
  const componentMap = useAppRegistryStore(s => s.componentMap);

  // Source the client-side entity pool from the org-wide nodes map + any cached
  // full entity bodies. This gives instant fuzzy results across ALL entities in
  // the org, not just the current space.
  const orgNodes = useOrgNodes();
  // Fallback pool: the current space's already-loaded entities. Used only while
  // the org Y.Doc is still connecting (orgNodes empty) so the modal isn't blank
  // on first open / reconnect.
  const spaceEntitiesFallback = useSpaceEntities(currentSpaceId);
  const canUseExperimental = useUserStore((s) => s.experimentalFeaturesEnabled === true);
  // Guests can never create any entity type, so the quickbar's "New Doc/Note/
  // Sheet/Canvas/Slides" shortcuts are never a usable action for them — hide
  // them rather than letting the click attempt-and-fail.
  const quickbarItems = useMemo(() => (isGuestSession() ? [] : QUICKBAR_ITEMS), []);
  // Build a merged array: full bodies from all caches take priority; lightweight
  // stubs (name + entityType from nodes map) fill in entities not yet cached.
  const allEntities = useMemo(() => {
    // Org Y.Doc not yet synced — show the current space's cached entities so the
    // modal has instant results instead of an empty list.
    if (orgNodes.size === 0) {
      return spaceEntitiesFallback.filter(e =>
        !!e.entityType &&
        !HIDDEN_ENTITY_TYPES.has(e.entityType) &&
        !e.entityType.startsWith('Integration.') &&
        !isExperimentallyGatedEntityType(e.entityType, canUseExperimental) &&
        (e as any).trashed !== true &&
        (e as any).hidden !== true &&
        (e as any).skeleton?.hidden !== true,
      );
    }
    const out: BaseEntityType[] = [];
    for (const [entityId, meta] of orgNodes) {
      if (!meta.entityType || HIDDEN_ENTITY_TYPES.has(meta.entityType)) continue;
      if (meta.entityType.startsWith('Integration.')) continue;
      if (isExperimentallyGatedEntityType(meta.entityType, canUseExperimental)) continue;
      if (meta.hidden === true) continue; // e.g. the per-space system "tmp" folder
      const full = entityRegistry.getById(entityId);
      if (full) {
        out.push(full as BaseEntityType);
      } else {
        // Lightweight stub sufficient for fuzzy name matching.
        out.push({
          _id: entityId,
          id: entityId,
          name: meta.name,
          entityType: meta.entityType,
          icon: meta.icon,
          skeleton: {},
          created_at: '',
          updated_at: '',
        } as unknown as BaseEntityType);
      }
    }
    return out;
  }, [orgNodes, spaceEntitiesFallback, canUseExperimental]);
  const { getEntityIcon } = useEntityIcon({ size: 16 });

  // Creates a minimal space and navigates to it with an optional URL suffix.
  // Used when the modal is triggered from outside a space (e.g. Home page).
  //
  // Guests can never create a Space (backend forbids it outright — see
  // routes/spaces.py's forbid_guests), and none of this component's actions
  // are an AI request or a system-bar click, so this must never surface the
  // account-claim modal. Instead of attempting-then-blocking, redirect into
  // the guest's own existing preview Space (they always have exactly one)
  // with the same deep-link suffix — the create/open/launch keeps working,
  // it just lands in the space they already have instead of a new one.
  const createSpaceAndNavigate = useCallback(async (urlSuffix: string, spaceName: string) => {
    if (isGuestSession()) {
      const guestSpaceId = getGuestSpaceId();
      if (guestSpaceId) {
        navigate(`/desktops/${guestSpaceId}${urlSuffix}`);
      }
      return;
    }
    if (!requireAccount('create a new desktop')) return;
    try {
      const { default: useSystemStore } = await import('../../../stores/systemStore');
      const newSpace = await useSystemStore.getState().actions.createSpace({
        name: spaceName,
        skipDefaultWindow: true,
        skipProjectLimit: true,
        settings: {
          isChatVisible: false,
          viewMode: 'focused',
          sidebar: { isVisible: true, width: 300, mode: 'normal' },
        },
      });
      if (newSpace?.id) {
        navigate(`/desktops/${newSpace.id}${urlSuffix}`);
      }
    } catch (err) {
      log.error('[GlobalEntitySearch] Failed to create space:', err);
    }
  }, [navigate]);
  const getNode = useDataspaceTreeStore(s => s.getNode);

  // Build id → entity map once per cache change (replaces the per-call linear
  // `allEntities.find(...)` that made space resolution O(n) per lookup), plus a
  // sibling entityId → owning-spaceId cache rebuilt in lockstep. Every node on
  // an entity's path up to its Space ancestor shares that same Space, so we
  // Build a fast O(1) id→entity map for parent-name lookups.
  const entityById = useMemo(() => {
    const map = new Map<string, BaseEntityType>();
    allEntities.forEach(e => { if (e._id) map.set(e._id, e); });
    return map;
  }, [allEntities]);

  const getParentName = useCallback((entityId: string): string | null => {
    const node = getNode(entityId);
    if (!node?.parentId) return null;
    return entityById.get(node.parentId)?.name || null;
  }, [getNode, entityById]);

  // Client-side results — fuzzy-scored for better typo/initials tolerance.
  // In global filesystem mode all org entities are in the pool so no space
  // bias is applied — recency sort for the empty-query case.
  const clientResults = useMemo((): BaseEntityType[] => {
    if (!query) {
      return [...allEntities]
        .filter(e => !e.trashed && !(e as any).hidden)
        .sort((a, b) => {
          const da = new Date(a.updated_at || a.created_at || 0).getTime();
          const db = new Date(b.updated_at || b.created_at || 0).getTime();
          return db - da;
        })
        .slice(0, 10);
    }
    return allEntities
      .filter(e => !e.trashed && !(e as any).hidden)
      .map(e => ({ entity: e, baseScore: Math.max(fuzzyScore(e.name || '', query), fuzzyScore(e.entityType || '', query)) }))
      .filter(({ baseScore }) => baseScore > 0)
      .map(({ entity, baseScore }) => ({ entity, score: baseScore }))
      .sort((a, b) => b.score - a.score)
      .map(({ entity }) => entity)
      .slice(0, 12);
  }, [allEntities, query]);

  // Debounced backend search — uses universal search (navigational mode) so that
  // content inside Docs, PDFs, and Notes is searched via content_chunks, not just
  // entity-level fields. Falls back to entity-only search if universal fails.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputValue.trim() || inputValue.trim().length < 2) {
      abortRef.current?.abort();
      setBackendResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setBackendLoading(true);
      try {
        const response = await searchUniversal({
          query: inputValue.trim(),
          mode: 'navigational',
          includePassages: false,
          limit: 20,
        });
        if (abortRef.current !== ac) return;
        // Map UniversalSearchResult to EntitySearchResult shape for existing rendering code
        const mapped: EntitySearchResult[] = (response.results ?? []).map(r => ({
          entity_id: r.entity_id,
          name: r.name,
          entityType: r.entity_type,
          icon: r.icon,
          score: r.score,
          snippet: r.snippet ?? undefined,
        }));
        setBackendResults(mapped);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        // Fall back to entity-only search on unexpected errors
        try {
          const results = await entityService.searchEntities(inputValue.trim(), undefined, 20, false);
          if (abortRef.current === ac) setBackendResults(results);
        } catch {
          setBackendResults([]);
        }
      } finally {
        if (abortRef.current === ac) setBackendLoading(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Track shift key so the selected-item label and footer hint update live
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftHeld(true); };
    const onUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftHeld(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  // Dedup backend extras against client results
  const clientIds = useMemo(() => new Set(clientResults.map(e => e._id)), [clientResults]);
  const backendExtras = useMemo((): EntitySearchResult[] => {
    if (!query) return [];
    return backendResults.filter(r =>
      !clientIds.has(r.entity_id) &&
      !isExperimentallyGatedEntityType(r.entityType, canUseExperimental),
    );
  }, [backendResults, clientIds, query, canUseExperimental]);

  // Entity matches (client + backend) — drives the "Ask River" fallback and the
  // "Content matches" label. Apps/Actions are counted separately for navigation.
  const totalCount = clientResults.length + backendExtras.length;

  // Launchable apps — fuzzy-matched against name and appId.
  const launchableApps = useMemo((): LaunchableApp[] => {
    return Object.values(appsCatalog)
      .map(a => a.skeleton)
      .filter((s): s is NonNullable<typeof s> =>
        !!s && !!s.appId && !!componentMap[s.appId] && !LAUNCHABLE_APP_BLOCKLIST.has(s.appId))
      .map(s => ({ appId: s.appId, name: s.name || s.appId, description: s.description || '' }));
  }, [appsCatalog, componentMap]);

  const matchingApps = useMemo((): LaunchableApp[] => {
    if (!query) return [];
    return launchableApps
      .map(app => ({ app, score: Math.max(fuzzyScore(app.name, query), fuzzyScore(app.appId, query)) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ app }) => app)
      .slice(0, 5);
  }, [launchableApps, query]);

  // Create actions — sourced from the entity creation registry (Automation, Doc,
  // Note, Sheet, …). Matched against the display name and "new/create <name>".
  const creationActions = useMemo(() => entityCreationRegistry.getAll(), []);

  const matchingActions = useMemo((): EntityCreationMetadata[] => {
    // Guests can never create any entity type (blocked outright by the backend's
    // guest-write gate, regardless of whether they have an active space), so the
    // "Actions" ("New X") section is never a usable result for them.
    if (!query || isGuestSession()) return [];
    // Strip a leading create-verb ("new doc" → "doc") so the verb itself doesn't
    // match — otherwise injecting "create <name>" into the haystack would make a
    // query like "c" match every action via the literal word "create".
    const stripped = query.replace(/^(new|create|add|make)\s+/i, '').trim() || query;
    const sl = stripped.toLowerCase();
    const scoreAction = (name: string): number => {
      const base = Math.max(fuzzyScore(name, query), fuzzyScore(name, stripped));
      if (base > 0) return base;
      // Tolerate singular/plural: a plural query ("automations") should still
      // surface the singular action ("Automation"). fuzzyScore only checks
      // name.includes(query), so we add the reverse direction here.
      const nl = name.toLowerCase();
      if (nl.length >= 3 && sl.includes(nl)) return 60;
      return 0;
    };
    return creationActions
      .map(c => ({ c, score: Math.max(scoreAction(c.displayName), scoreAction(c.entityType)) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ c }) => c)
      .slice(0, 5);
  }, [creationActions, query]);

  // Drive views — fuzzy-matched against view labels and "drive <label>"
  const matchingDriveViews = useMemo((): DriveViewItem[] => {
    if (!query) return [];
    return DRIVE_VIEW_ITEMS
      .map(item => ({ item, score: Math.max(fuzzyScore(item.label, query), fuzzyScore(`drive ${item.label}`, query)) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item);
  }, [query]);

  // Reset zone & selection on query change
  useEffect(() => {
    setActiveZone(null);
    setSelectedIndex(0);
  }, [inputValue]);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // When opened from a Cmd+click on the spatial canvas, register a one-shot
  // placement so the first entity created/opened lands where the user clicked.
  // The placement is consumed by the next createWindow (and auto-expires via a
  // TTL), so we deliberately never clear it here: action handlers call onClose()
  // — which nulls createPosition — BEFORE the async window creation runs, so
  // clearing on null would wipe the placement before it could be consumed.
  const createPosition = useGlobalEntitySearchStore(s => s.createPosition);
  useEffect(() => {
    if (createPosition) setNextWindowPlacement(createPosition);
  }, [createPosition]);

  // Scroll selected result into view
  useEffect(() => {
    if (activeZone === 'results') {
      listRef.current?.querySelector('.global-entity-search-item.selected')
        ?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, activeZone]);

  // Scroll focused quickbar item into view
  useEffect(() => {
    if (activeZone === 'quickbar') {
      quickbarRef.current?.querySelector('.quickbar-item.focused')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [quickbarIndex, activeZone]);

  // ── Quickbar actions ──────────────────────────────────────────────────────

  const fireQuickbarItem = useCallback(async (item: QuickbarItem) => {
    onClose();
    const spaceId = currentSpaceId;

    // Create items — when no active space, store intent and create space
    const CREATE_COMMAND_MAP: Record<string, string> = {
      'new-doc':    'doc',
      'new-note':   'note',
      'new-canvas': 'canvas',
      'new-sheet':  'sheet',
      'new-slides': 'slides',
    };
    const createType = CREATE_COMMAND_MAP[item.id];
    if (createType) {
      if (!hasActiveSpace) {
        sessionStorage.setItem('pendingEntityCreate', JSON.stringify({ type: createType }));
        createSpaceAndNavigate('', item.label);
      } else {
        let result: any;
        if (item.id === 'new-doc') {
          result = await executeCommand('docs_create', { name: 'Untitled Document', spaceId, createWindow: true });
        } else if (item.id === 'new-note') {
          result = await executeCommand('notes_create', { spaceId, createWindow: true });
        } else if (item.id === 'new-canvas') {
          result = await executeCommand('canvas_create', { title: 'Untitled Canvas', spaceId, createWindow: true });
        } else if (item.id === 'new-sheet') {
          result = await executeCommand('sheets_create', { spaceId, createWindow: true });
        } else if (item.id === 'new-slides') {
          result = await executeCommand('presentations_create', { spaceId, createWindow: true });
        }
        const windowId = result?.data?.window?.id;
        if (windowId) {
          useSpaceStore.getState().setActiveWindow(windowId);
        }
      }
    }
  }, [executeCommand, onClose, currentSpaceId, hasActiveSpace, createSpaceAndNavigate]);

  // ── Entity selection ──────────────────────────────────────────────────────
  //
  // Two modes:
  //   handleJumpTo  — navigate to the entity in a dedicated space (Enter, click)
  //   handleOpenHere — open a window for the entity in the current space (Shift+Enter, Shift+click)
  //
  // When there is no active space both actions fall back to navigate.

  const handleOpenHere = useCallback((entity: BaseEntityType) => {
    log.log('[GlobalEntitySearch] Open here:', entity.name, entity.entityType);
    onClose();

    if (!hasActiveSpace) {
      createSpaceAndNavigate(`?openEntity=${entity._id}`, entity.name || 'Space');
      return;
    }

    if (
      entity.entityType === 'Group' ||
      entity.entityType === 'Integration' ||
      entity.entityType?.startsWith('Integration.')
    ) {
      executeCommand('space_createWindow', {
        appType: 'entityBrowser2',
        title: entity.name || 'Folder',
        entity,
        callback: (result: string) => {
          try {
            const parsed = JSON.parse(result);
            if (parsed.success) log.log('[GlobalEntitySearch] EntityBrowser opened:', parsed);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_createWindow result:', err);
          }
        },
      });
    } else {
      executeCommand('space_open_window', {
        entityId: entity._id,
        callback: (result: string) => {
          try {
            const parsed = JSON.parse(result);
            if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open entity:', parsed.error);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_open_window result:', err);
          }
        },
      });
    }
  }, [executeCommand, onClose, hasActiveSpace, createSpaceAndNavigate]);

  const handleJumpTo = useCallback((entity: BaseEntityType) => {
    log.log('[GlobalEntitySearch] Jump to:', entity.name, entity.entityType);
    // Always open in the current desktop — navigating away to a different space
    // is jarring in the global filesystem model. The entity is accessible from
    // any desktop via the alias-edge mechanism. handleOpenHere calls onClose().
    handleOpenHere(entity);
  }, [handleOpenHere]);

  const handleOpenHereBackend = useCallback((result: EntitySearchResult) => {
    const full = allEntities.find(e => e._id === result.entity_id);
    if (full) { handleOpenHere(full); return; }

    onClose();
    if (!hasActiveSpace) {
      createSpaceAndNavigate(`?openEntity=${result.entity_id}`, result.name || 'Space');
      return;
    }
    executeCommand('space_open_window', {
      entityId: result.entity_id,
      callback: (res: string) => {
        try {
          const parsed = JSON.parse(res);
          if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open backend entity:', parsed.error);
        } catch (err) {
          log.error('[GlobalEntitySearch] Failed to parse result:', err);
        }
      },
    });
  }, [allEntities, handleOpenHere, onClose, executeCommand, hasActiveSpace, createSpaceAndNavigate]);

  const handleJumpToBackend = useCallback((result: EntitySearchResult) => {
    const full = allEntities.find(e => e._id === result.entity_id);
    if (full) { handleJumpTo(full); return; }
    // Open in current desktop — no space navigation in global filesystem mode.
    handleOpenHereBackend(result);
  }, [allEntities, handleJumpTo, handleOpenHereBackend]);

  const handleSelectDriveView = useCallback(async (item: DriveViewItem) => {
    onClose();
    if (!hasActiveSpace) {
      sessionStorage.setItem('pendingAppOpen', JSON.stringify({ appId: 'entityBrowser2', appName: item.label }));
      createSpaceAndNavigate(`?openApp=entityBrowser2`, item.label);
      return;
    }
    const newWindowId = await useSpaceStore.getState().actions.createWindow({
      appType: 'entityBrowser2',
      title: item.label,
      applicationState: { drive: { activeView: item.view } },
    });
    useSpaceStore.getState().setActiveWindow(newWindowId);
  }, [onClose, hasActiveSpace, createSpaceAndNavigate]);

  // Launch an app from the "Apps" results section. In a space we open a window;
  // on Home we create a space and hand off via ?openApp= (same as the quickbar).
  const handleLaunchApp = useCallback(async (app: LaunchableApp) => {
    log.log('[GlobalEntitySearch] Launch app:', app.appId);
    onClose();
    if (!hasActiveSpace) {
      sessionStorage.setItem('pendingAppOpen', JSON.stringify({ appId: app.appId, appName: app.name }));
      createSpaceAndNavigate(`?openApp=${app.appId}`, app.name);
      return;
    }
    const windowId = await useSpaceStore.getState().actions.createWindow({ appType: app.appId, title: app.name });
    useSpaceStore.getState().setActiveWindow(windowId);
  }, [onClose, hasActiveSpace, createSpaceAndNavigate]);

  // Run a create action from the "Actions" results section. In a space we
  // execute the create command then open the new entity's window when the
  // registry marks it `openAfterCreate` — mirroring SpaceSidebarV2's create
  // flow. We can't rely on `createWindow: true` because some create commands
  // (e.g. automation_create) return an entityId without opening a window.
  // On Home we defer the create via sessionStorage (consumed by Space.tsx once
  // the new space mounts).
  const handleRunCreate = useCallback(async (action: EntityCreationMetadata) => {
    log.log('[GlobalEntitySearch] Create:', action.commandId);
    onClose();
    if (!hasActiveSpace) {
      sessionStorage.setItem('pendingEntityCreate', JSON.stringify({ commandId: action.commandId }));
      createSpaceAndNavigate('', action.displayName);
      return;
    }
    const result: any = await executeCommand(action.commandId, { spaceId: currentSpaceId, createWindow: false });
    const entityId = result?.data?.entityId;
    if (result?.status === 'success' && entityId && action.openAfterCreate) {
      executeCommand('space_open_window', { entityId }).catch((err: any) => {
        log.error('[GlobalEntitySearch] Failed to open created entity:', err);
      });
    } else if (result && result.status !== 'success') {
      log.error('[GlobalEntitySearch] Create command failed:', action.commandId, result?.message);
    }
  }, [onClose, hasActiveSpace, createSpaceAndNavigate, executeCommand, currentSpaceId]);

  // Fires the prompt inside the current space via the spacechat event bus.
  // Falls back to creating a new space if there is no active space.
  const handleLaunchInCurrentSpace = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    onClose();
    if (hasActiveSpace) {
      window.dispatchEvent(new CustomEvent('spacechat:sendPrompt', {
        detail: { prompt: text, threadTitle: text.slice(0, 60) },
      }));
    } else {
      // Gate before the optimistic navigate below — otherwise a blocked guest
      // lands on a phantom "New Desktop" URL that was never actually created
      // server-side (launchAgentInNewSpace bails out silently in the background).
      if (!requireAccount('create a new desktop')) return;
      const { prepareAgentLaunch, launchAgentInNewSpace } = await import('../../../services/agentFireService');
      const ids = prepareAgentLaunch({ prompt: text });
      navigate(`/desktops/${ids.spaceId}?thread=${ids.threadId}`, {
        state: {
          isPendingSpace: true,
          fromHome: true,
          cachedSpace: ids.cachedSpace,
          threadId: ids.threadId,
          activeRunId: ids.runId,
          assistantMsgId: ids.assistantMsgId,
          optimisticUserMessage: ids.userMessage,
        },
      });
      interceptLLMRequest(
        'chat',
        () => launchAgentInNewSpace(ids, { prompt: text }),
        { userPrompt: text, operation: 'global_entity_search', selectedText: undefined },
      ).catch((err) => {
        log.error('[GlobalEntitySearch] Background launch failed:', err);
      });
    }
  }, [inputValue, onClose, hasActiveSpace, navigate]);

  // Always creates a new space for the prompt regardless of current context.
  const handleLaunchInNewSpace = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    onClose();
    // Same reasoning as handleLaunchInCurrentSpace's no-active-space branch —
    // gate before the optimistic navigate so guests never land on a phantom URL.
    if (!requireAccount('create a new desktop')) return;
    const { prepareAgentLaunch, launchAgentInNewSpace } = await import('../../../services/agentFireService');
    const ids = prepareAgentLaunch({ prompt: text });
    navigate(`/desktops/${ids.spaceId}?thread=${ids.threadId}`, {
      state: {
        isPendingSpace: true,
        fromHome: true,
        cachedSpace: ids.cachedSpace,
        threadId: ids.threadId,
        activeRunId: ids.runId,
        assistantMsgId: ids.assistantMsgId,
        optimisticUserMessage: ids.userMessage,
      },
    });
    interceptLLMRequest(
      'chat',
      () => launchAgentInNewSpace(ids, { prompt: text }),
      { userPrompt: text, operation: 'global_entity_search', selectedText: undefined },
    ).catch((err) => {
      log.error('[GlobalEntitySearch] Background launch failed:', err);
    });
  }, [inputValue, onClose, navigate]);

  // Opens a SearchResults entity window with the current search results.
  const handleLaunchSearch = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    onClose();

    const buildSearchEntity = async (spaceId?: string) => {
      const backendIds = new Set(backendResults.map(r => r.entity_id));
      const clientExtras = clientResults.filter(e => !backendIds.has(e._id));
      const mergedResults = [
        ...backendResults.map(r => ({
          id: r.entity_id, type: 'entity', entityType: r.entityType,
          name: r.name, icon: r.icon || '📄', description: r.snippet || r.entityType || '',
          metadata: { matchType: 'server', matchScore: r.score || 0, lastModified: undefined, path: '' },
        })),
        ...clientExtras.map(e => ({
          id: e._id, type: 'entity', entityType: e.entityType,
          name: e.name || 'Unnamed', icon: '📄', description: e.entityType || '',
          metadata: { matchType: 'client', matchScore: 0, lastModified: undefined, path: '' },
        })),
      ];
      const timestamp = new Date().toISOString();
      return entityActions.createEntity({
        entityType: 'SearchResults',
        name: `Search: ${text}`,
        ...(spaceId ? { spaceId } : {}),
        skeleton: {
          '@type': 'SearchResults',
          query: text,
          results: mergedResults,
          timestamp,
          resultCount: mergedResults.length,
          history: {
            entries: [{ query: text, results: mergedResults, timestamp, resultCount: mergedResults.length }],
            index: 0,
          },
        },
      });
    };

    if (!hasActiveSpace) {
      try {
        const { default: useSystemStore } = await import('../../../stores/systemStore');
        const newSpace = await useSystemStore.getState().actions.createSpace({
          name: `Search: ${text}`,
          skipDefaultWindow: true,
          skipProjectLimit: true,
          settings: { isChatVisible: false, viewMode: 'focused', sidebar: { isVisible: true, width: 300, mode: 'normal' } },
        });
        if (newSpace?.id) {
          const entity = await buildSearchEntity(newSpace.id);
          const entityId = (entity as any)._id || (entity as any).id;
          navigate(`/desktops/${newSpace.id}?openEntity=${entityId}`);
        }
      } catch (err) {
        log.error('[GlobalEntitySearch] Failed to launch search in new space:', err);
      }
      return;
    }

    try {
      const entity = await buildSearchEntity(currentSpaceId);
      const entityId = (entity as any)._id || (entity as any).id;
      executeCommand('space_open_window', {
        entityId,
        callback: (res: string) => {
          try {
            const parsed = JSON.parse(res);
            if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open search window:', parsed.error);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_open_window result:', err);
          }
        },
      });
    } catch (err) {
      log.error('[GlobalEntitySearch] Failed to launch search:', err);
    }
  }, [inputValue, onClose, currentSpaceId, hasActiveSpace, backendResults, clientResults, navigate, executeCommand]);

  const isEmpty = !query;
  const resultRowCount =
    clientResults.length + matchingApps.length + matchingActions.length +
    backendExtras.length + matchingDriveViews.length;
  const hasResults = resultRowCount > 0;
  const showLaunchAsPrompt = !isEmpty && (query.length > 30 || totalCount === 0);
  // "Search" opens a dedicated window backed by a persisted SearchResults
  // entity (handleLaunchSearch's entityActions.createEntity) — a write guests
  // can never make, active space or not. The live fuzzy results above already
  // give guests real search functionality, so hide just this derived action.
  const showSearch = !isEmpty && !isGuestSession();
  const selectedActionLabel = isShiftHeld && hasActiveSpace ? 'Open here' : 'Jump to';
  // Linear selection model (top → bottom):
  //   [prompt?] [search?] entities, apps, actions, content matches, drive views
  const promptOffset = showLaunchAsPrompt ? 1 : 0;
  const entityOffset = promptOffset + (showSearch ? 1 : 0);
  const appsStart = entityOffset + clientResults.length;
  const actionsStart = appsStart + matchingApps.length;
  const backendStart = actionsStart + matchingActions.length;
  const driveStart = backendStart + backendExtras.length;
  const navCount = driveStart + matchingDriveViews.length;

  // ── Keyboard navigation ───────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeZone === null) {
        setActiveZone('quickbar');
        setQuickbarIndex(0);
      } else if (activeZone === 'quickbar') {
        setActiveZone('results');
        setSelectedIndex(0);
      } else {
        setSelectedIndex(i => Math.min(i + 1, navCount - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeZone === 'results' && selectedIndex === 0) {
        setActiveZone('quickbar');
        setQuickbarIndex(0);
      } else if (activeZone === 'results') {
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (activeZone === 'quickbar') {
        setActiveZone(null);
      }
    } else if (e.key === 'ArrowLeft' && activeZone === 'quickbar') {
      e.preventDefault();
      setQuickbarIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'ArrowRight' && activeZone === 'quickbar') {
      e.preventDefault();
      setQuickbarIndex(i => Math.min(i + 1, quickbarItems.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Cmd+Enter always launches in a new space when the prompt item is visible
      if (e.metaKey && showLaunchAsPrompt) {
        handleLaunchInNewSpace();
        return;
      }
      // Shift+Enter opens in this space; plain Enter jumps to the entity
      const openHere = e.shiftKey && hasActiveSpace;
      if (activeZone === 'quickbar') {
        const item = quickbarItems[quickbarIndex];
        if (item) fireQuickbarItem(item);
      } else if (activeZone === 'results') {
        if (showLaunchAsPrompt && selectedIndex === 0) {
          handleLaunchInCurrentSpace();
        } else if (showSearch && selectedIndex === promptOffset) {
          handleLaunchSearch();
        } else if (selectedIndex < appsStart) {
          const entity = clientResults[selectedIndex - entityOffset];
          if (entity) { if (openHere) handleOpenHere(entity); else handleJumpTo(entity); }
        } else if (selectedIndex < actionsStart) {
          const app = matchingApps[selectedIndex - appsStart];
          if (app) handleLaunchApp(app);
        } else if (selectedIndex < backendStart) {
          const action = matchingActions[selectedIndex - actionsStart];
          if (action) handleRunCreate(action);
        } else if (selectedIndex < driveStart) {
          const be = backendExtras[selectedIndex - backendStart];
          if (be) { if (openHere) handleOpenHereBackend(be); else handleJumpToBackend(be); }
        } else {
          const dv = matchingDriveViews[selectedIndex - driveStart];
          if (dv) handleSelectDriveView(dv);
        }
      } else {
        // No zone active — fire first entity, then app, action, backend, drive, search, prompt
        if (clientResults.length > 0) {
          if (openHere) handleOpenHere(clientResults[0]); else handleJumpTo(clientResults[0]);
        } else if (matchingApps.length > 0) {
          handleLaunchApp(matchingApps[0]);
        } else if (matchingActions.length > 0) {
          handleRunCreate(matchingActions[0]);
        } else if (backendExtras.length > 0) {
          if (openHere) handleOpenHereBackend(backendExtras[0]); else handleJumpToBackend(backendExtras[0]);
        } else if (matchingDriveViews.length > 0) {
          handleSelectDriveView(matchingDriveViews[0]);
        } else if (showSearch) {
          handleLaunchSearch();
        } else if (showLaunchAsPrompt) {
          handleLaunchInCurrentSpace();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [
    activeZone, quickbarIndex, selectedIndex, entityOffset, promptOffset, navCount,
    appsStart, actionsStart, backendStart, driveStart, quickbarItems,
    clientResults, backendExtras, matchingApps, matchingActions, matchingDriveViews,
    showLaunchAsPrompt, showSearch, hasActiveSpace,
    fireQuickbarItem, handleJumpTo, handleOpenHere, handleJumpToBackend, handleOpenHereBackend,
    handleSelectDriveView, handleLaunchApp, handleRunCreate,
    handleLaunchInCurrentSpace, handleLaunchInNewSpace, handleLaunchSearch, onClose,
  ]);

  return (
    <div className="global-entity-search-overlay" onClick={onClose}>
      <div className="global-entity-search" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="global-entity-search-header">
          <MagnifyingGlass size={20} className="search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="global-entity-search-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search everything..."
            autoComplete="off"
            spellCheck={false}
          />
          {backendLoading && <span className="search-spinner" />}
        </div>

        {/* ── Quickbar ── */}
        {quickbarItems.length > 0 && (
        <div
          className={`global-entity-search-quickbar${activeZone === 'quickbar' ? ' is-active' : ''}`}
          ref={quickbarRef}
        >
          <div className="quickbar-creates">
            {quickbarItems.map((item, i) => (
              <button
                key={item.id}
                className={`quickbar-item${activeZone === 'quickbar' && quickbarIndex === i ? ' focused' : ''}`}
                onMouseDown={e => { e.preventDefault(); fireQuickbarItem(item); }}
                onMouseEnter={() => { setActiveZone('quickbar'); setQuickbarIndex(i); }}
                tabIndex={-1}
                title={item.label}
              >
                <span className="quickbar-item-icon">{item.icon}</span>
                <span className="quickbar-item-label">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* ── Results ── */}
        <div className="global-entity-search-results" ref={listRef}>
          {!hasResults && isEmpty && !showLaunchAsPrompt ? (
            <div className="global-entity-search-empty">No entities available</div>
          ) : (
            <>
              {/* Prompt item always at top when visible */}
              {showLaunchAsPrompt && (
                <div
                  className={`global-entity-search-item launch-as-prompt ${activeZone === 'results' && selectedIndex === 0 ? 'selected' : ''}`}
                  onClick={handleLaunchInCurrentSpace}
                  onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(0); }}
                >
                  <div className="entity-icon">
                    <Sparkle size={16} weight="duotone" />
                  </div>
                  <div className="entity-content">
                    <div className="entity-name">Ask River</div>
                    <div className="entity-path">{query.length > 50 ? `${query.slice(0, 50)}…` : query}</div>
                  </div>
                  <button
                    className="launch-in-new-space-chip"
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => { e.stopPropagation(); handleLaunchInNewSpace(); }}
                    tabIndex={-1}
                    title="Open in new desktop"
                  >
                    <ArrowUpRight size={10} />
                    New space
                    <span className="chip-hotkey">⌘↵</span>
                  </button>
                </div>
              )}

              {/* Search item — always shown when there is a query */}
              {showSearch && (
                <div
                  className={`global-entity-search-item launch-as-search ${activeZone === 'results' && selectedIndex === promptOffset ? 'selected' : ''}`}
                  onClick={handleLaunchSearch}
                  onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(promptOffset); }}
                >
                  <div className="entity-icon">
                    <MagnifyingGlass size={16} weight="duotone" />
                  </div>
                  <div className="entity-content">
                    <div className="entity-name">Search</div>
                    <div className="entity-path">{query.length > 50 ? `${query.slice(0, 50)}…` : query}</div>
                  </div>
                </div>
              )}

              {!hasResults && !isEmpty && (
                <div className="global-entity-search-empty">Nothing found for "{query}"</div>
              )}

              {isEmpty && (
                <div className="search-section-label">
                  <Clock size={11} weight="bold" /> Recent
                </div>
              )}

              {clientResults.map((entity, i) => {
                const itemIdx = i + entityOffset;
                const parentName = getParentName(entity._id);
                const pathText = parentName ? `in ${parentName}` : null;
                const isSelected = activeZone === 'results' && itemIdx === selectedIndex;
                return (
                  <div
                    key={entity._id}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => e.shiftKey && hasActiveSpace ? handleOpenHere(entity) : handleJumpTo(entity)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(itemIdx); }}
                  >
                    <div className="entity-icon">
                      {(entity.entityType === 'AppInstance' || entity.entityType === 'System.AppInstance')
                        ? <AppSquircle appId={(entity.skeleton as any)?.appId || ''} size="sm" />
                        : getEntityIcon(entity)}
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">{entity.name || 'Unnamed'}</div>
                      {pathText && <div className="entity-path">{pathText}</div>}
                    </div>
                    <span className="entity-type-label">{isSelected ? selectedActionLabel : typeLabel(entity.entityType || '')}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {matchingApps.length > 0 && (
                <div className="search-section-label">Apps</div>
              )}

              {matchingApps.map((app, i) => {
                const idx = appsStart + i;
                const isSelected = activeZone === 'results' && idx === selectedIndex;
                return (
                  <div
                    key={`app-${app.appId}`}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleLaunchApp(app)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">
                      <AppSquircle appId={app.appId} size="sm" />
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">{app.name}</div>
                      {app.description && <div className="entity-path">{app.description}</div>}
                    </div>
                    <span className="entity-type-label">{isSelected ? 'Open' : 'App'}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {matchingActions.length > 0 && (
                <div className="search-section-label">Actions</div>
              )}

              {matchingActions.map((action, i) => {
                const idx = actionsStart + i;
                const isSelected = activeZone === 'results' && idx === selectedIndex;
                return (
                  <div
                    key={`action-${action.commandId}`}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleRunCreate(action)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">
                      {getEntityIcon({ entityType: action.entityType, name: action.displayName, _id: action.commandId } as BaseEntityType)}
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">New {action.displayName}</div>
                      {action.description && <div className="entity-path">{action.description}</div>}
                    </div>
                    <span className="entity-type-label">{isSelected ? 'Create' : 'New'}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {backendExtras.length > 0 && (
                <div className="search-section-label">Content matches</div>
              )}

              {backendExtras.map((result, i) => {
                const idx = backendStart + i;
                const isSelected = activeZone === 'results' && idx === selectedIndex;
                const bParentName = getParentName(result.entity_id);
                const bPathText = bParentName ? `in ${bParentName}` : null;
                return (
                  <div
                    key={result.entity_id}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => e.shiftKey && hasActiveSpace ? handleOpenHereBackend(result) : handleJumpToBackend(result)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">
                      {getEntityIcon({ entityType: result.entityType, name: result.name, _id: result.entity_id } as BaseEntityType)}
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">{result.name}</div>
                      {bPathText
                        ? <div className="entity-path">{bPathText}</div>
                        : result.snippet && <div className="entity-path">{result.snippet}</div>
                      }
                    </div>
                    <span className="entity-type-label">{isSelected ? selectedActionLabel : typeLabel(result.entityType)}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {matchingDriveViews.length > 0 && (
                <div className="search-section-label">Drive</div>
              )}

              {matchingDriveViews.map((item, i) => {
                const idx = driveStart + i;
                const isSelected = activeZone === 'results' && idx === selectedIndex;
                return (
                  <div
                    key={item.id}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectDriveView(item)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">{item.icon}</div>
                    <div className="entity-content">
                      <div className="entity-name">{item.label}</div>
                      <div className="entity-path">Drive</div>
                    </div>
                    <span className="entity-type-label">{isSelected ? 'Open' : 'View'}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="global-entity-search-footer">
          <div className="keyboard-hints">
            <div className="hint">
              <span className="key">↑↓</span>
              <span>Navigate</span>
            </div>
            <div className="hint">
              <span className="key">←→</span>
              <span>Quickbar</span>
            </div>
            <div className={`hint${isShiftHeld && hasActiveSpace ? '' : ' hint--primary'}`}>
              <span className="key">↵</span>
              <span>Jump to</span>
            </div>
            {hasActiveSpace && (
              <div className={`hint${isShiftHeld ? ' hint--primary' : ''}`}>
                <span className="key">⇧↵</span>
                <span>Open here</span>
              </div>
            )}
            <div className="hint">
              <span className="key">esc</span>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalEntitySearch;
